import { orderTypeLabel } from '../orderTypeLabel.js';
import { esc } from '../escape.js';

const PREVIEW_COUNT = 8;

// Seconds → "m:ss" (or "h:mm:ss" past an hour) for the row timers.
const fmtClock = totalSecs => {
  const s = Math.max(0, totalSecs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

// Seconds → "04m 32s" (or "01h 04m 32s" past an hour) for the "Today so far"
// tiles. Returns markup: the unit letters are wrapped so they render smaller.
const fmtDuration = totalSecs => {
  const s = Math.max(0, Math.round(totalSecs));
  const pad = n => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const parts = [[Math.floor((s % 3600) / 60), 'm'], [s % 60, 's']];
  if (h > 0) parts.unshift([h, 'h']);
  return parts.map(([n, u]) => `${pad(n)}<i>${u}</i>`).join(' ');
};

const MORE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>';
const CHECK_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

class KDSView {
  _list          = document.getElementById('openOrdersList');
  _viewAllBtn    = document.getElementById('openOrdersViewAll');
  _expanded      = false;
  _modalBackdrop = document.getElementById('allOrdersBackdrop');
  _modalList     = document.getElementById('allOrdersModalList');
  _modalCount    = document.getElementById('allOrdersModalCount');
  _modalOpen     = false;
  _summary       = document.getElementById('oqSummary');
  _count         = document.getElementById('oqCount');
  _thr           = null; // last { yellow, red } seen by updateTimers

  // Queue timer thresholds (seconds). updateTimers receives the live values
  // every tick; before the first tick fall back to the same localStorage
  // keys/defaults model.state.settings reads, so the first paint is right.
  _thresholds() {
    return this._thr ?? {
      yellow: parseInt(localStorage.getItem('pointbunny_kds_yellow') || '600'),
      red: parseInt(localStorage.getItem('pointbunny_kds_red') || '900'),
    };
  }

  // Everything a row shows that changes with time. Urgency escalates on the
  // order's TOTAL age (a ready order sitting uncollected keeps aging toward
  // late - deliberate); the clock counts from ready-time for ready orders
  // ("how long has this sat uncollected"), from order-time otherwise.
  _rowState(order, now) {
    const { yellow, red } = this._thresholds();
    const age = Math.floor((now - order.startedAt) / 1000);
    const isReady = !!order.readyAt;
    const level = age >= red ? 'urgent' : age >= yellow ? 'warn' : 'ok';
    const shown = Math.floor((now - (isReady ? order.readyAt : order.startedAt)) / 1000);
    let note = '';
    if (isReady) note = 'waiting for pickup';
    else if (level === 'urgent') note = `over by ${fmtClock(age - red)}`;
    return {
      isReady,
      level,
      label: isReady ? 'Ready' : level === 'urgent' ? 'Late' : level === 'warn' ? 'Slow' : 'Order',
      clock: fmtClock(shown),
      note,
      pct: red > 0 ? Math.min(100, Math.round((age / red) * 100)) : 0,
    };
  }

  _rowClass(st, hidden) {
    return `oq-row${st.isReady ? ' oq-row--ready' : ''}${st.level !== 'ok' ? ` oq-row--${st.level}` : ''}${hidden ? ' oq-row--hidden' : ''}`;
  }

  // Header line under the title: "3 preparing · 1 ready · 1 late".
  _renderSummary(queue, now = Date.now()) {
    if (this._count) this._count.textContent = queue.length ? `${queue.length} order${queue.length !== 1 ? 's' : ''}` : '';
    if (!this._summary) return;
    if (!queue.length) { this._summary.classList.add('hidden'); return; }
    let ready = 0, late = 0;
    queue.forEach(o => {
      const st = this._rowState(o, now);
      if (st.isReady) ready++;
      if (st.level === 'urgent') late++;
    });
    const preparing = queue.length - ready;
    const html = `<span class="oq-sum oq-sum--prep"><i></i>${preparing} preparing</span>`
      + (ready ? `<span class="oq-sum oq-sum--ready"><i></i>${ready} ready</span>` : '')
      + (late ? `<span class="oq-sum oq-sum--late"><i></i>${late} late</span>` : '');
    // Called every timer tick: only touch the DOM when a count changed, so
    // the aria-live region announces real changes, not every second.
    if (this._summary.innerHTML !== html) this._summary.innerHTML = html;
    this._summary.classList.remove('hidden');
  }

  // ── "Today so far" (empty state) ──────────────────────────────────────────
  // Stats come from the controller's today totals query (fetchPeriodTotals).
  // Shown under the bunny only once at least one order has been completed
  // today; otherwise the empty state is just the bunny.
  _todayStats = null;

  setTodayStats(stats) {
    this._todayStats = stats ?? null;
    // Patch the open empty state in place - re-rendering it would restart
    // the bunny's animations.
    const empty = this._list.querySelector('.oq-empty-state');
    if (!empty) return;
    empty.querySelector('.oq-today')?.remove();
    empty.insertAdjacentHTML('beforeend', this._todayStatsMarkup());
  }

  _todayStatsMarkup() {
    const s = this._todayStats;
    if (!s?.completed) return '';
    const hourLabel = h => new Date(2000, 0, 1, h % 24).toLocaleTimeString('en-US', { hour: 'numeric' });
    // Done orders bucketed by the same queue timer settings that turn rows
    // Slow / Late: under "slow" = on time, under "late" = slow, past it = delayed.
    const { yellow, red } = this._thresholds();
    const timed = s.servingMinutes ?? [];
    const onTime = timed.filter(m => m * 60 < yellow).length;
    const delayed = timed.filter(m => m * 60 >= red).length;
    const slow = timed.length - onTime - delayed;
    const mins = secs => `${Math.round(secs / 60)} min`;
    const longest = s.longest ? fmtDuration(s.longest.minutes * 60) : null;
    const lastOut = s.lastDoneAt
      ? new Date(s.lastDoneAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : null;
    // Each tile opens Reports on the section that explains it.
    const tile = (section, tone, label, value, sub, aria) => `
      <button type="button" class="oq-today-tile oq-today-tile--${tone}" data-report-section="${section}" aria-label="${aria}, open in Reports">
        <small>${label}</small><b>${value}</b><span>${sub}</span>
      </button>`;
    return `
      <div class="oq-today">
        <span class="oq-today-label">Today so far</span>
        <div class="oq-today-tiles">
          ${tile('traffic', 'neutral', 'Total orders', s.completed, 'done today', `${s.completed} orders done today`)}
          ${tile('kitchen', 'good', 'On time', onTime, `under ${mins(yellow)}`, `${onTime} orders on time`)}
          ${tile('kitchen', 'warn', 'Slow', slow, `${Math.round(yellow / 60)}-${mins(red)}`, `${slow} slow orders`)}
          ${tile('kitchen', 'bad', 'Delayed', delayed, `over ${mins(red)}`, `${delayed} delayed orders`)}
        </div>
        <div class="oq-today-foot">
          ${longest ? `<span>Longest wait <b>${longest}</b>${s.longest.ticketNumber ? ` (#${s.longest.ticketNumber})` : ''}</span>` : ''}
          ${lastOut ? `<span>Last order out at <b>${lastOut}</b></span>` : ''}
          ${s.busiestHour ? `<button type="button" class="oq-today-link" data-report-section="traffic">Busiest hour: <b>${hourLabel(s.busiestHour.hour)} – ${hourLabel(s.busiestHour.hour + 1)}</b></button>` : ''}
        </div>
      </div>`;
  }

  _addHandlerTodayStats(openReports) {
    this._list.addEventListener('click', e => {
      const el = e.target.closest('.oq-today [data-report-section]');
      if (el) openReports(el.dataset.reportSection);
    });
  }

  renderQueue(queue) {
    this.closeStatusPopover(); // rows re-render; the popover's anchor is gone
    if (queue.length === 0) {
      // Rabbit body/feet/tail/ears (.oq-hop-rabbit) adapted from "Rabbit" by
      // Katy DeCorah / Beard Chicken: https://codepen.io/katydecorah/pen/nKGxJd
      // used under the MIT License - eyes/face removed and dust-puff dropped
      // per the approved direction below; hop/kick timing otherwise unchanged.
      this._list.innerHTML = `
        <li class="oq-empty-state">
          <div class="oq-empty-scene">
            <div class="oq-aura oq-aura--1" aria-hidden="true"></div>
            <div class="oq-aura oq-aura--2" aria-hidden="true"></div>
            <div class="oq-aura oq-aura--3" aria-hidden="true"></div>
            <span class="oq-particle oq-particle--1" aria-hidden="true"></span>
            <span class="oq-particle oq-particle--2" aria-hidden="true"></span>
            <span class="oq-particle oq-particle--3" aria-hidden="true"></span>
            <span class="oq-particle oq-particle--4" aria-hidden="true"></span>
            <span class="oq-particle oq-particle--5" aria-hidden="true"></span>
            <span class="oq-empty-item oq-empty-item--1" role="img" aria-label="Drink order">🥤</span>
            <span class="oq-empty-item oq-empty-item--2" role="img" aria-label="Dessert order">🍰</span>
            <span class="oq-empty-item oq-empty-item--3" role="img" aria-label="Food order">🍔</span>
            <div class="oq-runway-shadow" aria-hidden="true"></div>
            <div class="oq-hop-rabbit" aria-hidden="true"></div>
          </div>
          <span class="oq-empty-title">All caught up</span>
          <span class="oq-empty-subtitle">The queue is quiet&hellip; for now.<br>Get ready!</span>
          ${this._todayStatsMarkup()}
        </li>`;
      this._viewAllBtn.classList.add('hidden');
      this._expanded = false;
    } else {
      this._list.innerHTML = queue
        .map((order, i) => this._rowMarkup(order, i + 1, i >= PREVIEW_COUNT && !this._expanded))
        .join('');

      if (queue.length > PREVIEW_COUNT) {
        this._viewAllBtn.classList.remove('hidden');
        this._viewAllBtn.textContent = this._expanded
          ? 'Show Less'
          : `View All (${queue.length})`;
      } else {
        this._viewAllBtn.classList.add('hidden');
        this._expanded = false;
      }
    }

    this._renderSummary(queue);
    if (this._modalOpen) this._renderModalList(queue);
  }

  _renderModalList(queue) {
    if (this._modalCount) {
      this._modalCount.textContent = queue.length > 0
        ? `${queue.length} order${queue.length !== 1 ? 's' : ''}`
        : '';
    }
    if (queue.length === 0) {
      this._modalList.innerHTML = '<li class="oq-empty">No active orders.</li>';
      return;  // modal uses compact empty state
    }
    this._modalList.innerHTML = queue
      .map((order, i) => this._rowMarkup(order, i + 1, false))
      .join('');
  }

  openModal(queue) {
    this._modalOpen = true;
    this._modalBackdrop.classList.remove('hidden');
    this._renderModalList(queue);
  }

  closeModal() {
    this._modalOpen = false;
    this._modalBackdrop.classList.add('hidden');
  }

  _addHandlerOpenModal(getQueue) {
    document.getElementById('openOrdersExpandBtn')?.addEventListener('click', () => {
      this.openModal(getQueue());
    });
  }

  _addHandlerModalClose() {
    document.getElementById('allOrdersCloseBtn')?.addEventListener('click', () => this.closeModal());
    this._modalBackdrop?.addEventListener('click', e => {
      if (e.target === this._modalBackdrop) this.closeModal();
    });
  }

  // ── "Mark as…" mini-modal ──────────────────────────────────────────────────
  // ONE shared floating popover on <body> (same singleton pattern as
  // tooltip.js) so it never clips inside the queue panel or the All Orders
  // modal. Anchored to the clicked button, viewport-clamped, flips above when
  // there's no room below.
  _popover = null;
  _onReadyChoice = null;
  _onDoneChoice = null;
  _onUnreadyChoice = null;

  _ensurePopover() {
    if (this._popover) return this._popover;
    const el = document.createElement('div');
    el.className = 'status-popover';
    el.setAttribute('role', 'menu');
    el.innerHTML = `
      <p class="status-popover-title" data-pop-title></p>
      <button class="status-popover-ready" type="button" role="menuitem">🛎️ Ready for Pickup</button>
      <button class="status-popover-unready hidden" type="button" role="menuitem">↩ Back to Preparing</button>
      <button class="status-popover-done" type="button" role="menuitem">✓ Done</button>`;
    document.body.appendChild(el);
    el.querySelector('.status-popover-ready').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onReadyChoice) this._onReadyChoice(id);
    });
    el.querySelector('.status-popover-unready').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onUnreadyChoice) this._onUnreadyChoice(id);
    });
    el.querySelector('.status-popover-done').addEventListener('click', () => {
      const id = el.dataset.orderId;
      this.closeStatusPopover();
      if (id && this._onDoneChoice) this._onDoneChoice(id);
    });
    this._popover = el;
    return el;
  }

  _openStatusPopover(btn) {
    const el = this._ensurePopover();
    const isReady = btn.dataset.state === 'ready';
    el.dataset.orderId = btn.dataset.orderId;
    el.querySelector('[data-pop-title]').textContent = `Update order #${btn.dataset.ticket}`;
    // State-aware choices: preparing → Ready/Done; ready → Back to Preparing/Done
    // (Done takes the green when it's the natural next step).
    el.querySelector('.status-popover-ready').classList.toggle('hidden', isReady);
    el.querySelector('.status-popover-unready').classList.toggle('hidden', !isReady);
    el.classList.toggle('status-popover--ready', isReady);
    el.classList.add('status-popover--open');
    const r = btn.getBoundingClientRect();
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    const left = Math.min(Math.max(8, r.right - pw), window.innerWidth - pw - 8);
    let top = r.bottom + 6;
    if (top + ph > window.innerHeight - 8) top = r.top - ph - 6;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  closeStatusPopover() {
    if (!this._popover) return;
    this._popover.classList.remove('status-popover--open');
    delete this._popover.dataset.orderId;
  }

  // One delegated listener per list covers every row action: the one-tap next
  // step (Mark ready / Done), the ⋯ "more" mini-modal trigger, the direct
  // Mark Done on ready rows, and the cashier's "back to Preparing" revert.
  _wireActions(root, onDone, onReady, onUnready) {
    this._onReadyChoice = onReady;
    this._onDoneChoice = onDone;
    this._onUnreadyChoice = onUnready;
    root?.addEventListener('click', e => {
      const next = e.target.closest('.oq-next-btn');
      if (next) {
        this.closeStatusPopover();
        return next.dataset.action === 'done' ? onDone(next.dataset.orderId) : onReady(next.dataset.orderId);
      }
      const statusBtn = e.target.closest('.oq-status-btn');
      if (statusBtn) {
        const isOpenForThis = this._popover?.classList.contains('status-popover--open')
          && this._popover.dataset.orderId === statusBtn.dataset.orderId;
        if (isOpenForThis) this.closeStatusPopover();
        else this._openStatusPopover(statusBtn);
        return;
      }
      const done = e.target.closest('.oq-done-btn');
      if (done) return onDone(done.dataset.orderId);
      const unready = e.target.closest('.oq-unready-btn');
      if (unready) return onUnready(unready.dataset.orderId);
    });
  }

  _addHandlerActions(onDone, onReady, onUnready) {
    this._wireActions(this._list, onDone, onReady, onUnready);
  }

  _addHandlerModalActions(onDone, onReady, onUnready) {
    this._wireActions(this._modalList, onDone, onReady, onUnready);
  }

  // Outside click, Escape, scroll, or resize dismisses the mini-modal (its
  // anchor may have moved or gone).
  _addHandlerChooserDismiss() {
    document.addEventListener('click', e => {
      if (e.target.closest('.status-popover') || e.target.closest('.oq-status-btn')) return;
      this.closeStatusPopover();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeStatusPopover();
    });
    document.addEventListener('scroll', () => this.closeStatusPopover(), true);
    window.addEventListener('resize', () => this.closeStatusPopover());
  }

  // One line per item, quantity first ("2× Iced Latte · Large"). The first two
  // items show; the rest collapse into "+N more" on the second line.
  _itemLines(items) {
    const lines = items.map(it => {
      const variants = (it.selectedVariants ?? []).map(v => v?.optionName).filter(Boolean);
      return `${it.quantity ?? 1}× ${esc(it.itemName)}${variants.length ? ` <span class="oq-item-opt">· ${esc(variants.join(', '))}</span>` : ''}`;
    });
    const shown = lines.slice(0, 2);
    const extra = lines.length - shown.length;
    return shown.map((l, i) => `<span class="oq-item">${l}${extra > 0 && i === shown.length - 1 ? ` <span class="oq-item-more">+${extra} more</span>` : ''}</span>`).join('');
  }

  _rowMarkup(order, num, hidden) {
    const orderTypeEnabled = localStorage.getItem('pointbunny_order_type_enabled') !== 'false';
    const typeLabel = (!orderTypeEnabled || !order.orderType) ? '' : orderTypeLabel(order.orderType);
    const st = this._rowState(order, Date.now());
    // A tab's kitchen ticket shows its table instead of a ticket number.
    const ticket = order.fromTab && order.tableName ? esc(order.tableName) : (order.ticketNumber ?? num);

    return `
      <li class="${this._rowClass(st, hidden)}" data-order-id="${order.id}">
        <div class="oq-tkt"><small class="oq-tkt-label">${st.label}</small><b>${ticket}</b></div>
        <div class="oq-body">
          <div class="oq-meta">
            ${typeLabel && !order.fromTab ? `<span class="oq-type">${typeLabel}</span>` : ''}
            ${order.tableName ? `<span class="oq-type oq-table">Table ${esc(order.tableName)}</span>` : ''}
            ${order.practice ? '<span class="oq-practice" title="Tutorial practice order: not saved, disappears when the tutorial ends">🎓 Practice</span>' : ''}
            <span class="oq-clock">${st.clock}</span>
            <span class="oq-note">${st.note}</span>
          </div>
          ${this._itemLines(order.items)}
        </div>
        <div class="oq-actions">
          <button class="oq-next-btn${st.isReady ? ' oq-next-btn--done' : ''}" type="button" data-order-id="${order.id}" data-action="${st.isReady ? 'done' : 'ready'}">${st.isReady ? `${CHECK_ICON}Done` : 'Mark ready'}</button>
          <button class="oq-status-btn oq-more-btn" type="button" data-order-id="${order.id}" data-ticket="${ticket}" data-state="${st.isReady ? 'ready' : 'preparing'}" aria-label="More actions for order ${ticket}" title="More actions">${MORE_ICON}</button>
        </div>
        <div class="oq-track" aria-hidden="true"><i style="width:${st.pct}%"></i></div>
      </li>`;
  }

  updateTimers(queue, now, yellowThreshold, redThreshold) {
    this._thr = { yellow: yellowThreshold, red: redThreshold };
    const roots = this._modalOpen
      ? [this._list, this._modalList]
      : [this._list];

    queue.forEach(order => {
      const st = this._rowState(order, now);
      roots.forEach(root => {
        const row = root.querySelector(`.oq-row[data-order-id="${order.id}"]`);
        if (!row) return;
        row.className = this._rowClass(st, row.classList.contains('oq-row--hidden'));
        row.querySelector('.oq-tkt-label').textContent = st.label;
        row.querySelector('.oq-clock').textContent = st.clock;
        row.querySelector('.oq-note').textContent = st.note;
        row.querySelector('.oq-track > i').style.width = `${st.pct}%`;
      });
    });
    this._renderSummary(queue, now);
  }

  playNewOrderSound() {
    try {
      const ctx = new AudioContext();
      const frequencies = [523.25, 659.25, 783.99];
      frequencies.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.07);
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + i * 0.07 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.07 + 0.35);
        osc.start(ctx.currentTime + i * 0.07);
        osc.stop(ctx.currentTime + i * 0.07 + 0.35);
      });
    } catch (_) {}
  }

  _addHandlerViewAll() {
    this._viewAllBtn.addEventListener('click', () => {
      this._expanded = !this._expanded;
      Array.from(this._list.querySelectorAll('.oq-row')).forEach((row, i) => {
        row.classList.toggle('oq-row--hidden', !this._expanded && i >= PREVIEW_COUNT);
      });
      this._viewAllBtn.textContent = this._expanded ? 'Show Less' : `View All`;
    });
  }

  showUndoToast(order, onUndo, durationMs) {
    const label = order.tableName ? `Table ${esc(order.tableName)}` : order.ticketNumber ? `#${order.ticketNumber}` : 'Order';
    const el = document.createElement('div');
    el.className = 'toast toast--undo';
    let remaining = Math.ceil(durationMs / 1000);

    el.innerHTML = `
      <span class="toast-undo-msg">${label} marked done</span>
      <span class="toast-undo-countdown">${remaining}s</span>
      <button class="toast-undo-btn" type="button">Undo</button>`;
    el.querySelector('.toast-undo-btn').addEventListener('click', onUndo, { once: true });

    let container = document.getElementById('undoToastContainer');
    if (!container) {
      container = document.createElement('div');
      container.id = 'undoToastContainer';
      container.className = 'undo-toast-container';
      document.body.appendChild(container);
    }
    container.appendChild(el);
    void el.offsetHeight;
    el.classList.add('toast--visible');

    const countdownInterval = setInterval(() => {
      remaining--;
      const cd = el.querySelector('.toast-undo-countdown');
      if (cd) cd.textContent = `${remaining}s`;
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
      el.classList.remove('toast--visible');
      el.addEventListener('transitionend', () => {
        el.remove();
        const c = document.getElementById('undoToastContainer');
        if (c && c.children.length === 0) c.remove();
      }, { once: true });
    };
  }
}

export default new KDSView();
